import { useQueryClient } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { addDays, format, parse, subDays } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import Button from "@/components/Button";
import ErrorText from "@/components/ErrorText";
import FormField from "@/components/FormField";
import FormFields from "@/components/FormFields";
import Header from "@/components/Header";
import Icon from "@/components/Icon";
import { Input } from "@/components/Input";
import Label from "@/components/Label";
import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { ErrorMessages } from "@/constants/messages";
import { PageRoute, PageTitle } from "@/constants/pages";
import { setAppointment, useAppointmentQuery } from "@/hooks/appointments";
import { DashboardQueryKey, useRescheduleMutation } from "@/hooks/booking";
import AvailableTimesPicker from "@/pages/Booking/AvailableTimesPicker";
import { Appointment } from "@/services/booking";
import { isSetAndNotEmpty } from "@/utils/helpers";

export const ReschedulePage = () => {
  const [searchParams] = useSearchParams();

  const appointmentQuery = useAppointmentQuery(
    searchParams.get("appointment") ?? undefined,
  );

  useDocumentTitle(PageTitle.APPOINTMENT_RESCHEDULE);

  if (appointmentQuery.isPending) {
    return <Loader />;
  }

  if (appointmentQuery.isError) {
    return <ErrorText error={ErrorMessages.GENERIC} center />;
  }

  let appointment: Appointment;
  switch (appointmentQuery.data.type) {
    case "error":
      return <ErrorText error={ErrorMessages.GENERIC} center />;
    case "notCustomersAppointment": {
      return <ErrorText error={ErrorMessages.INCORRECT_ACCOUNT} center />;
    }
    case "success": {
      appointment = appointmentQuery.data.appointment;
      break;
    }
  }

  if (!appointment.canReschedule) {
    return (
      <ErrorText error={ErrorMessages.APPOINTMENT_CANNOT_RESCHEDULE} center />
    );
  }

  return <Content appointment={appointment} />;
};

const Schema = z.object({
  date: z.string(),
  time: z
    .object({
      display_time: z.string(),
      appointment_datetime: z.string(),
    })
    .nullable(),
});

type Schema = z.infer<typeof Schema>;

const Content = (props: { appointment: Appointment }) => {
  const { appointment } = props;
  const today = format(new Date(), "yyyy-MM-dd");
  const { register, handleSubmit, control, watch, setValue } = useForm<Schema>({
    defaultValues: {
      date: today,
    },
  });

  const queryClient = useQueryClient();
  const rescheduleMutation = useRescheduleMutation();
  const navigate = useNavigate();
  const notify = useNotification();

  const date = watch("date");
  const time = watch("time");

  const onSubmit = handleSubmit((data) => {
    const time = data.time?.appointment_datetime;
    if (!isSetAndNotEmpty(time)) return;
    rescheduleMutation.mutate(
      {
        appointmentId: appointment.id,
        time: time,
      },
      {
        onSuccess: (res) => {
          switch (res.type) {
            case "success": {
              notify("Appointment rescheduled successfully", "success");
              setAppointment(res.appointment, queryClient);
              void queryClient.refetchQueries({
                queryKey: DashboardQueryKey,
              });
              navigate(PageRoute.HOME, { replace: true });
              break;
            }
            case "timeTaken": {
              notify(ErrorMessages.TIME_TAKEN, "error");
              break;
            }
            case "error": {
              notify(ErrorMessages.GENERIC, "error");
              break;
            }
          }
        },
      },
    );
  });

  return (
    <form onSubmit={onSubmit}>
      <Header className={"mb-8"}>
        Reschedule at {appointment.store_description}
      </Header>

      <div className={"mb-4 inline-flex flex-row justify-end"}>
        <p className={"inline font-sans"}>
          <span className={"inline font-sans font-semibold"}>Service:</span>
          &nbsp;
          {appointment.service_description}
        </p>
      </div>

      <FormFields>
        <FormField className="mb-4">
          <Label htmlFor="date">Date</Label>
          <Input id="date" type="date" min={today} {...register("date")} />
        </FormField>
        <FormField className="mb-6">
          <div className="flex items-center justify-center gap-5 text-studer-blue">
            <button
              type="button"
              onClick={() => {
                if (date === today) {
                  return;
                }
                setValue(
                  "date",
                  format(
                    subDays(parse(date, "yyyy-MM-dd", new Date()), 1),
                    "yyyy-MM-dd",
                  ),
                );
              }}
              aria-label="Previous day"
              aria-disabled={date === today}
              className="p-4 focus:outline-amalfi-coast aria-disabled:cursor-not-allowed aria-disabled:text-gray-300"
            >
              <Icon name="arrow" aria-hidden />
            </button>
            <div className="text-xl font-semibold">
              {format(parse(date, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")}
            </div>
            <button
              type="button"
              onClick={() =>
                setValue(
                  "date",
                  format(
                    addDays(parse(date, "yyyy-MM-dd", new Date()), 1),
                    "yyyy-MM-dd",
                  ),
                )
              }
              className="p-4 focus:outline-amalfi-coast"
              aria-label="Next day"
            >
              <Icon name="arrow" className="rotate-180" aria-hidden />
            </button>
          </div>

          <Controller
            control={control}
            name="time"
            render={({ field: { name, value } }) => (
              <AvailableTimesPicker
                time={value}
                storeCode={appointment.store_code}
                serviceId={appointment.service_id}
                date={date ?? undefined}
                name={name}
                onChange={(_, time) => {
                  setValue("time", time);
                }}
              />
            )}
          />
        </FormField>

        {date && time && <Button type="submit">Confirm</Button>}
      </FormFields>
    </form>
  );
};
